import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { services, serviceTabs, caseStudies, testimonials, blogPosts, awards, stats } from "../mockData";
import { usePopup } from "../context/PopupContext";

/* ─── Hero Section ─── */
function HeroSection() {
  const { open } = usePopup();
  const heroSliderImages = [
    // Web Design Images
    "/images/portfolio-web-1.png",
    "/images/portfolio-web-2.png",
    "/images/portfolio-web-3.png",
    "/images/portfolio-web-4.png",
    "/images/portfolio-web-5.png",
    "/images/portfolio-web-6.png",
    "/images/portfolio-web-7.png",
    "/images/portfolio-web-8.png",
    "/images/portfolio-web-9.png",
    "/images/portfolio-web-10.png",
    "/images/portfolio-web-11.png",
    "/images/portfolio-web-12.png",
    "/images/portfolio-web-14.png",
    "/images/portfolio-web-15.png",
    "/images/portfolio-web-16.png",
    "/images/portfolio-web-17.png",
    "/images/portfolio-web-18.png",
    
    // e-Commerce Images
    "/images/portfolio-e-com-1.png",
    "/images/portfolio-e-com-2.png",
    "/images/portfolio-e-com-3.png",
    "/images/portfolio-e-com-4.png",
    "/images/portfolio-e-com-5.png",
    "/images/portfolio-e-com-6.png",
    "/images/portfolio-e-com-7.png",
    "/images/portfolio-e-com-8.png",
    "/images/portfolio-e-com-9.png",
    "/images/portfolio-e-com-10.png",
    "/images/portfolio-e-com-11.png",
    "/images/portfolio-e-com-12.png",
    "/images/portfolio-e-com-13.png",
    "/images/portfolio-e-com-14.png",
    "/images/portfolio-e-com-15.png",
    "/images/portfolio-e-com-16.png",
    
    // Web Apps Images
    "/images/portfolio-app-1.png",
    "/images/portfolio-app-2.png",
    "/images/portfolio-app-3.png",
    "/images/portfolio-app-4.png",
    "/images/portfolio-app-5.png",
    "/images/portfolio-app-6.png",
    "/images/portfolio-app-7.png",
    "/images/portfolio-app-8.png",
    "/images/portfolio-app-9.png",
    "/images/portfolio-app-10.png",
    "/images/portfolio-app-11.png",
    "/images/portfolio-app-12.jpg",
    "/images/portfolio-app-13.png",
    "/images/portfolio-app-14.png",
    "/images/portfolio-app-15.png",
    "/images/portfolio-app-16.png",
    "/images/portfolio-app-17.jpg",
    "/images/portfolio-app-18.jpg",
    "/images/portfolio-app-19.jpg",
    "/images/portfolio-app-20.jpg",
    "/images/portfolio-app-21.jpg",
    "/images/portfolio-app-22.png",
    
    // Branding Images
    "/images/portfolio-brand-1.png",
    "/images/portfolio-brand-2.png",
    "/images/portfolio-brand-3.png",
    "/images/portfolio-brand-4.png",
    "/images/portfolio-brand-5.png",
    "/images/portfolio-brand-6.png",
    "/images/portfolio-brand-7.png",
    "/images/portfolio-brand-8.png",
    "/images/portfolio-brand-9.png",
    "/images/portfolio-brand-10.png",
    "/images/portfolio-brand-11.png",
    "/images/portfolio-brand-12.png",
    "/images/portfolio-brand-13.png",
    "/images/portfolio-brand-14.png",
    "/images/portfolio-brand-15.png",
    "/images/portfolio-brand-16.png",
    
    // Logo Images
    "/images/portfolio-logo-1.jpg",
    "/images/portfolio-logo-2.jpg",
    "/images/portfolio-logo-4.jpg",
    "/images/portfolio-logo-5.jpg",
    "/images/portfolio-logo-6.jpg",
    "/images/portfolio-logo-7.jpg",
    "/images/portfolio-logo-8.jpg",
    "/images/portfolio-logo-9.jpg",
    "/images/portfolio-logo-10.jpg",
    "/images/portfolio-logo-11.jpg",
    "/images/portfolio-logo-12.jpg",
    "/images/portfolio-logo-13.jpg",
    "/images/portfolio-logo-14.jpg",
    "/images/portfolio-logo-15.jpg",
    "/images/portfolio-logo-17.jpg",
    "/images/portfolio-logo-18.jpg",
    "/images/portfolio-logo-19.jpg",
    "/images/portfolio-logo-20.jpg",
    "/images/portfolio-logo-21.jpg",
    "/images/portfolio-logo-23.jpg",
    "/images/portfolio-logo-24.jpg",
    "/images/portfolio-logo-25.jpg",
    "/images/portfolio-logo-26.jpg",
    "/images/portfolio-logo-27.jpg",
    "/images/portfolio-logo-28.jpg",
    "/images/portfolio-logo-29.jpg",
    "/images/portfolio-logo-30.jpg",
    "/images/portfolio-logo-31.jpg",
    "/images/portfolio-logo-32.jpg"
  ];

  return (
    <section data-testid="hero-section" className="relative min-h-screen flex flex-col items-center justify-center pt-32 pb-16 px-4 overflow-hidden">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-purple-900/10 via-transparent to-transparent pointer-events-none" />
      
      {/* Small badge */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="flex items-center gap-2 bg-purple-900/20 border border-purple-800/30 rounded-full px-5 py-2 mb-8"
      >
        <span className="text-gray-300 text-sm">Trusted by 200+ businesses worldwide for digital excellence</span>
        <ArrowRight size={14} className="text-purple-400" />
      </motion.div>

      {/* Main heading */}
      <motion.h1
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, delay: 0.1 }}
        className="text-4xl sm:text-5xl lg:text-6xl font-light text-center text-white max-w-5xl leading-tight font-['Playfair_Display']"
      >
        Empowering businesses{" "}
        <br className="hidden sm:block" />
        with next-gen <span className="text-purple-400">digital</span> solutions
      </motion.h1>

      {/* Subtitle */}
      <motion.p
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="text-gray-400 text-base sm:text-lg mt-6 text-center max-w-xl"
      >
        From strategy to execution, we deliver end-to-end consulting that drives measurable growth and lasting transformation.
      </motion.p>

      {/* CTA */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.4 }}
      >
        <button
          onClick={open}
          className="mt-10 inline-flex items-center gap-2 cta-button text-white px-8 py-4 rounded-full text-base font-semibold"
          data-testid="hero-cta"
        >
          Start Your Digital Journey
        </button>
      </motion.div>

      {/* Service images slider */}
      <div className="mt-16 w-full overflow-hidden">
        <div className="flex animate-marquee-slow">
          {[...heroSliderImages, ...heroSliderImages, ...heroSliderImages].map((img, i) => (
            <div key={i} className="flex-shrink-0 mx-3 w-[200px] sm:w-[240px] md:w-[280px]">
              <div className="rounded-3xl overflow-hidden phone-glow border border-purple-900/30 bg-[#12121f]">
                <img
                  src={img}
                  alt={`Service showcase ${i + 1}`}
                  className="w-full h-[380px] sm:h-[420px] md:h-[480px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Rating badge */}
      <div className="mt-8 text-gray-400 text-sm">
        <span className="text-yellow-400">★★★★★</span> 4.9/5 From 3,602 Customers
      </div>
    </section>
  );
}

/* ─── Brand Logos Marquee ─── */
function BrandMarquee() {
  const brands = ["Google", "Microsoft", "Amazon", "Meta", "Apple", "Shopify", "Stripe", "Slack", "Figma", "Notion"];
  return (
    <section className="py-12 border-y border-purple-900/10 overflow-hidden">
      <div className="flex animate-marquee">
        {[...brands, ...brands, ...brands].map((brand, i) => (
          <div key={i} className="flex-shrink-0 mx-10 flex items-center">
            <span className="text-gray-500 text-lg font-semibold tracking-wider opacity-50">{brand}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Mission Section ─── */
function MissionSection() {
  const offerings = [
    {
      title: "Web Development",
      description: "Responsive, user-eccentric websites enhancing client engagement and growth with modern frameworks and cutting-edge technology.",
      image: "https://images.unsplash.com/photo-1547658719-da2b51169166?w=600&h=400&fit=crop"
    },
    {
      title: "App Development",
      description: "Building scalable apps designed to perform seamlessly across all devices with native and cross-platform expertise.",
      image: "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=600&h=400&fit=crop"
    },
    {
      title: "AI Development",
      description: "Cutting-edge AI solutions that enhance decision-making, automate workflows, and streamline business processes.",
      image: "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=400&fit=crop"
    },
    {
      title: "Digital Marketing",
      description: "Comprehensive digital marketing strategies that drive brand visibility, engagement, and measurable ROI.",
      image: "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=600&h=400&fit=crop"
    }
  ];

  return (
    <section className="py-24 px-4 bg-[#f8f8fc]" data-testid="mission-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-6">
          Beyond software — we architect <span className="text-purple-600">business evolution</span>
        </h2>
        <p className="text-gray-600 text-base sm:text-lg max-w-3xl mx-auto leading-relaxed text-center mb-14">
          Digitronix Consultancy combines deep industry expertise with cutting-edge technology to deliver solutions that don't just digitize processes — they redefine how enterprises operate, compete, and win.
        </p>

        {/* Cards - Slider on mobile, Grid on desktop */}
        <div className="lg:grid lg:grid-cols-4 lg:gap-6 mobile-slider">
          {offerings.map((item, i) => (
            <div
              key={i}
              className="w-[260px] sm:w-[280px] lg:w-auto rounded-2xl overflow-hidden card-hover bg-white border border-gray-200 shadow-sm"
            >
              <img src={item.image} alt={item.title} className="w-full h-48 object-cover" loading="lazy" />
              <div className="p-6">
                <h3 className="text-gray-900 font-semibold text-lg mb-2">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Services Section ─── */
function ServicesSection() {
  const [activeTab, setActiveTab] = useState(0);
  const currentService = services[activeTab];
  const { open } = usePopup();

  const serviceImages = [
    "https://images.unsplash.com/photo-1551650975-87deedd944c3?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1639762681485-074b7f938ba0?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1547658719-da2b51169166?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1432888622747-4eb9a8efeb07?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1677442136019-21780ecad995?w=1200&h=600&fit=crop",
    "https://images.unsplash.com/photo-1499750310107-5fef28a66643?w=1200&h=600&fit=crop",
  ];

  return (
    <section id="services" className="py-24 px-4 bg-[#08080f]" data-testid="services-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-16">
          Services engineered to scale{" "}
          <span className="gradient-text">your ambition</span> globally
        </h2>

        {/* Tabs - scrollable on mobile */}
        <div className="flex gap-3 sm:gap-6 mb-12 overflow-x-auto pb-2 scrollbar-hide sm:flex-wrap sm:justify-center sm:overflow-visible">
          {serviceTabs.map((tab, i) => (
            <button
              key={tab}
              onClick={() => setActiveTab(i)}
              className={`text-sm font-medium pb-2 transition-all whitespace-nowrap ${
                i === activeTab ? "text-white service-tab-active" : "text-gray-500 hover:text-gray-300"
              }`}
              data-testid={`service-tab-${i}`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Service Content */}
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="relative rounded-3xl overflow-hidden min-h-[450px] flex items-center"
        >
          {/* Background image */}
          <div className="absolute inset-0">
            <img
              src={serviceImages[activeTab]}
              alt=""
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a1a]/90 via-[#0a0a1a]/70 to-transparent" />
          </div>
          
          {/* Content */}
          <div className="relative z-10 p-8 sm:p-12 max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-semibold text-white mb-4">{currentService.title}</h3>
            <p className="text-gray-300 text-base mb-8">{currentService.description}</p>
            <div className="flex gap-10 mb-8">
              {currentService.stats.map((stat, i) => (
                <div key={i} className="border-l border-purple-500/50 pl-4">
                  <span className="text-3xl sm:text-4xl font-bold text-white">{stat.value}</span>
                  <p className="text-gray-400 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
            <button
              onClick={open}
              className="inline-flex items-center gap-2 cta-button text-white px-8 py-4 rounded-full text-sm font-semibold"
              data-testid="service-cta"
            >
              View Service <ArrowRight size={16} />
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Stats Section ─── */
function StatsSection() {
  const statIcons = ["</>;", "♥", "✓", "⊞"];
  return (
    <section className="py-24 px-4 bg-[#f8f8fc]" data-testid="stats-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          <span className="text-purple-600">We help businesses</span> unlock their true{" "}
          potential <span className="text-purple-600">and grow</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
          We provide digital solutions that streamline operations, enhance customer engagement, and drive measurable results.
        </p>
        <div className="lg:grid lg:grid-cols-4 lg:gap-6 mobile-slider">
          {stats.map((stat, i) => (
            <div key={i} className="w-[240px] lg:w-auto bg-gradient-to-br from-purple-600 to-purple-800 rounded-2xl p-6 flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                <span className="text-white text-lg font-bold">{statIcons[i]}</span>
              </div>
              <div>
                <div className="text-white text-3xl font-bold">{stat.value}</div>
                <p className="text-white/80 text-sm mt-1">{stat.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Case Studies Section ─── */
function CaseStudiesSection() {
  const [activeSlide, setActiveSlide] = useState(0);
  const study = caseStudies[activeSlide];
  const { open } = usePopup();

  const next = () => setActiveSlide((prev) => (prev + 1) % caseStudies.length);
  const prev = () => setActiveSlide((prev) => (prev - 1 + caseStudies.length) % caseStudies.length);

  return (
    <section id="case-studies" className="py-24 px-4 bg-[#08080f]" data-testid="case-studies-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Where complex challenges meet <span className="text-purple-400">elegant solutions</span> and{" "}
          <span className="text-purple-400">flawless execution</span>
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-16">
          Every project we undertake is a testament to our commitment — blending creative vision with technical precision to deliver outcomes that exceed expectations.
        </p>

        {/* Case Study Card */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Right - Laptop Mockup (shows first on mobile) */}
          <div className="laptop-frame lg:order-2">
            <img
              src={study.image}
              alt={study.title}
              className="w-full h-[250px] sm:h-[380px] object-cover rounded-lg"
              loading="lazy"
            />
          </div>

          {/* Left Content */}
          <div className="lg:order-1 text-center lg:text-left">
            <h3 className="text-xl sm:text-2xl font-semibold text-white">{study.title}</h3>
            <span className="inline-block mt-2 bg-purple-600 text-white text-xs px-4 py-1 rounded-full">
              by Digitronix Consultancy
            </span>
            <p className="text-gray-400 mt-6 leading-relaxed text-sm sm:text-base">{study.description}</p>
            <div className="flex flex-wrap gap-3 mt-6 justify-center lg:justify-start">
              {study.tags.map((tag) => (
                <span key={tag} className="bg-white/10 text-gray-300 text-xs px-4 py-2 rounded-full border border-purple-900/20">{tag}</span>
              ))}
            </div>
            <div className="flex justify-center lg:justify-start">
              <button
                onClick={open}
                className="mt-8 inline-flex items-center gap-2 cta-button text-white px-6 py-3 rounded-full text-sm font-semibold"
                data-testid="case-study-cta"
              >
                Let's Discuss Your Project
              </button>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center items-center gap-4 mt-10">
          <button onClick={prev} className="text-purple-400 hover:text-purple-300"><ChevronLeft size={22} /></button>
          <div className="flex gap-2">
            {caseStudies.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveSlide(i)}
                className={`w-3 h-3 rounded-full transition-all ${
                  i === activeSlide ? "bg-purple-500 w-6" : "bg-gray-600"
                }`}
                data-testid={`case-dot-${i}`}
              />
            ))}
          </div>
          <button onClick={next} className="text-purple-400 hover:text-purple-300"><ChevronRight size={22} /></button>
        </div>
      </div>
    </section>
  );
}

/* ─── Awards Section ─── */
function AwardsSection() {
  return (
    <section className="py-24 px-4 bg-[#f8f8fc]" data-testid="awards-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Recognized globally for delivering{" "}
          <span className="text-purple-600">digital excellence</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
          Our relentless pursuit of innovation and quality has earned us recognition from leading industry organizations and publications worldwide.
        </p>
        <div className="lg:grid lg:grid-cols-6 lg:gap-6 mobile-slider">
          {awards.map((award) => (
            <div key={award.id} className="w-[160px] lg:w-auto bg-white rounded-2xl p-6 text-center card-hover award-glow border border-gray-200 min-h-[220px] flex flex-col items-center justify-center">
              <div className="w-20 h-20 mx-auto mb-4 bg-gray-50 rounded-2xl flex items-center justify-center border border-gray-100">
                <span className="text-purple-600 font-bold text-2xl">★</span>
              </div>
              <h4 className="text-gray-900 font-bold text-base">{award.name}</h4>
              <p className="text-gray-500 text-xs mt-2 leading-relaxed">{award.subtitle}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Testimonials Section ─── */
function TestimonialsSection() {
  return (
    <section className="py-24 px-4 bg-gradient-to-b from-[#0a0a14] to-[#08080f] overflow-hidden" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-white text-center mb-4">
          Trusted by leaders <span className="gradient-text">across industries</span>
        </h2>
        <p className="text-gray-400 text-center max-w-2xl mx-auto mb-16">
          Don't just take our word for it — hear directly from the executives and decision-makers who chose Digitronix as their technology partner.
        </p>
      </div>

      {/* Testimonial cards - Marquee */}
      <div className="overflow-hidden">
        <div className="flex animate-marquee">
          {[...testimonials, ...testimonials, ...testimonials].map((t, i) => (
            <div key={i} className="flex-shrink-0 w-[calc(100vw-2rem)] lg:w-[350px] mx-4 testimonial-card rounded-2xl p-6">
              <p className="text-gray-300 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
              <div className="flex items-center gap-3">
                <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" loading="lazy" />
                <div>
                  <h4 className="text-white font-semibold text-sm">{t.name}</h4>
                  <p className="text-gray-500 text-xs">{t.role}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ─── Blog Section ─── */
function BlogSection() {
  return (
    <section className="py-24 px-4 bg-[#f8f8fc]" data-testid="blog-section">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-4xl font-['Playfair_Display'] text-gray-900 text-center mb-4">
          Insights, strategies & <span className="text-purple-600">industry perspectives</span>
        </h2>
        <p className="text-gray-600 text-center max-w-2xl mx-auto mb-16">
          Explore our latest articles on emerging technologies, digital strategy, and business transformation from the minds behind Digitronix.
        </p>

        {/* Blog cards - slider on mobile, bento on desktop */}
        <div className="lg:hidden mobile-slider">
          {blogPosts.map((post) => (
            <Link key={post.id} to={`/blog/${post.slug}`} className="relative rounded-2xl overflow-hidden w-[260px] h-[280px] group cursor-pointer">
              <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
              <div className="absolute top-4 left-4 flex gap-2">
                <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full">{post.category}</span>
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <span className="text-purple-400 text-xs font-mono">{post.number}</span>
                <h3 className="text-white font-semibold text-base leading-tight mt-1">{post.title}</h3>
              </div>
            </Link>
          ))}
        </div>

        {/* Bento Grid Layout - desktop only */}
        <div className="hidden lg:grid grid-cols-3 gap-6 auto-rows-[280px]">
          {/* Left column - 2 cards */}
          <div className="flex flex-col gap-6">
            {blogPosts.slice(0, 2).map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="relative rounded-2xl overflow-hidden flex-1 group cursor-pointer">
                <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full">{post.category}</span>
                  <span className="bg-black/50 backdrop-blur text-gray-200 text-xs px-3 py-1 rounded-full">{post.date}</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div>
                    <span className="text-purple-400 text-xs font-mono">{post.number}</span>
                    <h3 className="text-white font-semibold text-lg leading-tight mt-1">{post.title}</h3>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight size={16} className="text-white" />
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {/* Center - Featured large card */}
          <Link to="/blog/guide-to-choosing-your-ai-partner" className="relative rounded-2xl overflow-hidden row-span-2 group cursor-pointer">
            <img src="https://images.unsplash.com/photo-1677442136019-21780ecad995?w=600&h=800&fit=crop" alt="Featured" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 right-6">
              <span className="bg-purple-600 text-white text-xs px-4 py-1.5 rounded-full">AI Solutions</span>
              <h3 className="text-white font-semibold text-xl leading-tight mt-4">Grow your business<br/>with AI & Automation</h3>
            </div>
          </Link>

          {/* Right column - 2 cards */}
          <div className="flex flex-col gap-6">
            {blogPosts.slice(2, 4).map((post) => (
              <Link key={post.id} to={`/blog/${post.slug}`} className="relative rounded-2xl overflow-hidden flex-1 group cursor-pointer">
                <img src={post.image} alt={post.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute top-4 left-4 flex gap-2">
                  <span className="bg-purple-600 text-white text-xs px-3 py-1 rounded-full">{post.category}</span>
                  <span className="bg-black/50 backdrop-blur text-gray-200 text-xs px-3 py-1 rounded-full">{post.date}</span>
                </div>
                <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <div>
                    <span className="text-purple-400 text-xs font-mono">{post.number}</span>
                    <h3 className="text-white font-semibold text-lg leading-tight mt-1">{post.title}</h3>
                  </div>
                  <div className="w-10 h-10 rounded-full bg-white/10 backdrop-blur flex items-center justify-center flex-shrink-0">
                    <ArrowUpRight size={16} className="text-white" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─── Main Home Page ─── */
export default function Home() {
  return (
    <main data-testid="home-page">
      <HeroSection />
      <MissionSection />
      <ServicesSection />
      <StatsSection />
      <CaseStudiesSection />
      <AwardsSection />
      <TestimonialsSection />
      <BlogSection />
    </main>
  );
}
